import Vue from "vue";
import VueI18n from "vue-i18n";

// Import locale files
import en from "@/locales/en.json";
import es from "@/locales/es.json";
import he from "@/locales/he.json";
import ru from "@/locales/ru.json";
import it from "@/locales/it.json";
import de from "@/locales/de.json";
import fr from "@/locales/fr.json";
import ar from "@/locales/ar.json";
import zh from "@/locales/zh.json";
import sv from "@/locales/sv.json";
import th from "@/locales/th.json";
import pl from "@/locales/pl.json";

Vue.use(VueI18n);

// Helper function to get language from URL
function getLanguageFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get("lang");

  // Check if it's a valid language
  if (
    langParam &&
    [
      "en",
      "es",
      "he",
      "ru",
      "it",
      "de",
      "fr",
      "ar",
      "zh",
      "sv",
      "th",
      "pl",
    ].includes(langParam)
  ) {
    return langParam;
  }

  // Check for language prefix in path (e.g., /es/listings)
  const pathParts = window.location.pathname.split("/");
  const pathLang = pathParts[1];
  if (
    pathLang &&
    [
      "en",
      "es",
      "he",
      "ru",
      "it",
      "de",
      "fr",
      "ar",
      "zh",
      "sv",
      "th",
      "pl",
    ].includes(pathLang)
  ) {
    return pathLang;
  }

  return null;
}

// Helper function to update URL with language
function updateUrlWithLanguage(locale: string) {
  const currentUrl = new URL(window.location.href);

  // Always add the language parameter to the URL
  currentUrl.searchParams.set("lang", locale);

  // Update the URL without reloading the page
  window.history.replaceState({}, "", currentUrl.toString());
}

// Get language from URL first, then localStorage, then default to 'en'
const urlLanguage = getLanguageFromUrl();
const savedLocale =
  urlLanguage || localStorage.getItem("selectedLanguage") || "en";

const i18n = new VueI18n({
  locale: savedLocale,
  fallbackLocale: "en",
  messages: {
    en,
    es,
    he,
    ru,
    it,
    de,
    fr,
    ar,
    zh,
    sv,
    th,
    pl,
  },
});

// Watch for locale changes and save to localStorage and URL
let currentLocale = savedLocale;

Object.defineProperty(i18n, "locale", {
  get() {
    return currentLocale;
  },
  set(newLocale: string) {
    currentLocale = newLocale;
    localStorage.setItem("selectedLanguage", newLocale);
    updateUrlWithLanguage(newLocale);

    // Set document direction for RTL languages
    if (newLocale === "he" || newLocale === "ar") {
      document.documentElement.setAttribute("dir", "rtl");
      document.documentElement.setAttribute("lang", newLocale);
    } else {
      document.documentElement.setAttribute("dir", "ltr");
      document.documentElement.setAttribute("lang", newLocale);
    }
  },
});

// Set initial document direction and URL
if (savedLocale === "he" || savedLocale === "ar") {
  document.documentElement.setAttribute("dir", "rtl");
  document.documentElement.setAttribute("lang", savedLocale);
} else {
  document.documentElement.setAttribute("dir", "ltr");
  document.documentElement.setAttribute("lang", savedLocale);
}

// ALWAYS update URL on initial load with the current language
updateUrlWithLanguage(savedLocale);

export default i18n;
